import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  AutocompleteOption,
  Box,
  Button,
  Chip,
  ChipDelete,
  FormControl,
  FormLabel,
  FormHelperText,
  ListItemDecorator,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Input,
  Stack,
} from '@mui/joy';
import { Tag as LocalOfferIcon, Plus as AddIcon } from '@phosphor-icons/react';
import type { Tag } from '../../types';

// Preset color palette (8 colors as per requirements)
const PRESET_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#10b981' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Lime', value: '#84cc16' },
];

interface TagPickerProps {
  value: number[]; // Array of selected tag IDs
  onChange: (tagIds: number[]) => void;
  availableTags: Tag[]; // All available tags (excluding is_deleted=true)
  maxTags?: number; // Maximum number of tags (default: 10)
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  onCreateTag?: (name: string, color: string) => Promise<Tag>; // Callback to create new tag
}

/**
 * TagPicker - Universal tag selection component with autocomplete and tag creation
 *
 * Features:
 * - Autocomplete with search
 * - Shows existing tags filtered by search
 * - "Create new tag" option if search doesn't match
 * - Color picker when creating new tags
 * - Multi-select with chips
 * - Max tag validation (default: 10)
 */
export const TagPicker: React.FC<TagPickerProps> = ({
  value,
  onChange,
  availableTags,
  maxTags = 10,
  placeholder = 'Select or create tags...',
  label = 'Tags',
  helperText,
  error = false,
  required = false,
  disabled = false,
  onCreateTag,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0].value);
  const [isCreating, setIsCreating] = useState(false);

  // Get selected tags from IDs
  const selectedTags = useMemo(
    () => (availableTags || []).filter((tag) => value.includes(tag.id)),
    [availableTags, value]
  );

  // Filter available tags alphabetically (excluding already selected and soft-deleted)
  const filteredTags = useMemo(() => {
    const tags = (availableTags || []);
    return tags
      .filter((tag) => !value.includes(tag.id) && !tag.is_deleted)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availableTags, value]);

  // Check if we should show "Create new tag" option
  const showCreateOption = useMemo(() => {
    const trimmedInput = inputValue.trim().toLowerCase();
    return (
      trimmedInput.length > 0 &&
      !filteredTags.some((tag) => tag.name.toLowerCase() === trimmedInput) &&
      !selectedTags.some((tag) => tag.name.toLowerCase() === trimmedInput)
    );
  }, [inputValue, filteredTags, selectedTags]);

  const handleTagSelect = (tag: Tag) => {
    if (value.length >= maxTags) {
      return; // Don't allow more than max tags
    }
    console.log('[TagPicker] Tag selected:', tag);
    onChange([...value, tag.id]);
    setInputValue(''); // Clear the input after selection
  };

  const handleTagRemove = (tagId: number) => {
    onChange(value.filter((id) => id !== tagId));
  };

  const handleOpenCreateModal = () => {
    setNewTagName(inputValue.trim());
    setShowCreateModal(true);
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim() || !onCreateTag) return;

    setIsCreating(true);
    try {
      const createdTag = await onCreateTag(newTagName.trim(), selectedColor);
      onChange([...value, createdTag.id]);
      setShowCreateModal(false);
      setNewTagName('');
      setInputValue('');
      setSelectedColor(PRESET_COLORS[0].value);
    } catch (error) {
      console.error('Failed to create tag:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const atMaxTags = value.length >= maxTags;

  // Available tags for dropdown - exclude already selected tags
  const autocompleteOptions = useMemo(() => {
    const filtered = (availableTags || [])
      .filter(tag => !tag.is_deleted && !value.includes(tag.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    console.log('[TagPicker] Autocomplete options calculated:', {
      totalAvailable: availableTags?.length || 0,
      selectedCount: value.length,
      filteredCount: filtered.length,
      filtered: filtered.map(t => t.name)
    });

    return filtered;
  }, [availableTags, value]);

  // Debug logging
  React.useEffect(() => {
    console.log('[TagPicker] Render:', {
      availableTags: availableTags?.length || 0,
      selectedTags: selectedTags.length,
      autocompleteOptions: autocompleteOptions.length,
      value,
      selectedTagObjects: selectedTags,
      atMaxTags
    });
  });

  return (
    <>
      <FormControl error={error} required={required}>
        {label && <FormLabel>{label}</FormLabel>}

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {selectedTags.map((tag) => (
              <Chip
                key={tag.id}
                size="sm"
                variant="solid"
                startDecorator={<LocalOfferIcon size={14} />}
                endDecorator={<ChipDelete aria-label={`Remove ${tag.name}`} disabled={disabled} onDelete={() => handleTagRemove(tag.id)} />}
                sx={{
                  backgroundColor: tag.color,
                  color: '#fff',
                }}
              >
                {tag.name}
              </Chip>
            ))}
          </Box>
        )}

        {/* Autocomplete for adding more tags */}
        {!atMaxTags && (
          <Autocomplete
            value={null}
            onChange={(_event, newValue) => {
              // When a tag is selected, add it to the list
              if (newValue) {
                handleTagSelect(newValue);
              }
            }}
            inputValue={inputValue}
            onInputChange={(_event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            options={autocompleteOptions}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            placeholder={placeholder}
            disabled={disabled || atMaxTags}
            slotProps={{
              listbox: {
                sx: {
                  zIndex: 99999, // Very high z-index to appear above modals
                  maxHeight: '300px',
                  overflow: 'auto'
                }
              }
            }}
            renderOption={(props, option) => (
              <AutocompleteOption {...props}>
                <ListItemDecorator>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: option.color,
                    }}
                  />
                </ListItemDecorator>
                {option.name}
              </AutocompleteOption>
            )}
            noOptionsText={
              showCreateOption && onCreateTag ? (
                <Box>
                  <Typography level="body-sm" sx={{ mb: 1 }}>
                    No matching tags found
                  </Typography>
                  <Button
                    size="sm"
                    variant="soft"
                    startDecorator={<AddIcon />}
                    onClick={handleOpenCreateModal}
                    fullWidth
                  >
                    Create "{inputValue.trim()}"
                  </Button>
                </Box>
              ) : (
                'No tags found'
              )
            }
          />
        )}

        {helperText && <FormHelperText>{helperText}</FormHelperText>}
        {atMaxTags && (
          <FormHelperText>
            Maximum {maxTags} tags reached. Remove a tag to add another.
          </FormHelperText>
        )}
      </FormControl>

      {/* Create Tag Modal */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Create New Tag
          </Typography>

          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Tag Name</FormLabel>
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
                autoFocus
              />
            </FormControl>

            <FormControl>
              <FormLabel>Color</FormLabel>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map((color) => (
                  <Box
                    key={color.value}
                    onClick={() => setSelectedColor(color.value)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      backgroundColor: color.value,
                      cursor: 'pointer',
                      border: selectedColor === color.value ? '3px solid' : '2px solid',
                      borderColor: selectedColor === color.value ? 'primary.500' : 'neutral.300',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                    title={color.name}
                  />
                ))}
              </Box>
            </FormControl>

            {/* Preview */}
            <Box>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Preview:
              </Typography>
              <Chip
                size="md"
                variant="solid"
                startDecorator={<LocalOfferIcon />}
                sx={{
                  backgroundColor: selectedColor,
                  color: '#fff',
                }}
              >
                {newTagName || 'Tag Name'}
              </Chip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="plain" color="neutral" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateTag}
                loading={isCreating}
                disabled={!newTagName.trim()}
              >
                Create Tag
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
};
